import { Component, Input, Output, EventEmitter, OnInit, AfterViewInit, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { ChatAIService, ChatMessage, Course } from '../../../services/chat-ai.service';
import { TranslateService } from '../../../services/translate.service';

@Component({
    selector: 'app-learning-assistant',
    templateUrl: './learning-assistant.component.html',
    styleUrls: ['./learning-assistant.component.scss']
})
export class LearningAssistantComponent implements OnInit, AfterViewInit, OnChanges {
    @Input() messages: ChatMessage[] = []; // Recibe los mensajes del padre
    @Output() messageAdded = new EventEmitter<ChatMessage>();
    @Output() courseSelected = new EventEmitter<boolean>(); // Emite true cuando se selecciona, false cuando se deselecciona

    @ViewChild('userInput') userInput!: ElementRef<HTMLTextAreaElement>;
    @ViewChild('contentContainer') contentContainer!: ElementRef;

    userQuery = '';
    isLoading = false;
    selectedCourse: Course | null = null;
    isSubscribing = false;
    private welcomeSent = false;

    constructor(private chatAIService: ChatAIService, private translateService: TranslateService) { }

    /**
     * Auto-resize del textarea cuando el usuario escribe
     */
    ngAfterViewInit(): void {
        if (this.userInput) {
            const textarea = this.userInput.nativeElement;
            textarea.addEventListener('input', () => {
                textarea.style.height = 'auto';
                textarea.style.height = textarea.scrollHeight + 'px';
            });
        }

        // Hacer scroll inicial al cargar el componente
        setTimeout(() => this.scrollToBottom(), 200);
    }

    ngOnInit(): void {
        // Mostrar mensaje de bienvenida solo si no hay mensajes previos
        if (!this.welcomeSent && this.messages.length === 0) {
            const welcomeMsg: ChatMessage = {
                id: this.generateId(),
                role: 'assistant',
                content: this.translateService.instant('chat.learning-assistant.welcome'),
                timestamp: Date.now(),
                type: 'text'
            };
            this.messageAdded.emit(welcomeMsg);
            this.welcomeSent = true;
        }
    }

    /**
     * Envía la consulta del usuario y obtiene sugerencias de cursos
     */
    sendQuery(): void {
        if (!this.userQuery.trim()) {
            return;
        }

        // Agregar mensaje del usuario
        const userMessage: ChatMessage = {
            id: this.generateId(),
            role: 'user',
            content: this.userQuery,
            timestamp: Date.now(),
            type: 'text'
        };

        this.messageAdded.emit(userMessage);
        this.isLoading = true;
        // No ocultar las recomendaciones anteriores

        console.log('🔍 Searching for courses:', this.userQuery);

        // Llamar al servicio de IA para obtener sugerencias
        this.chatAIService.getSuggestedCourses(this.userQuery).subscribe(
            (courses: Course[]) => {
                this.isLoading = false;
                console.log('✅ Received courses:', courses?.length || 0);

                // Validar si hay resultados
                if (!courses || courses.length === 0) {
                    console.log('⚠️ No courses found for query:', this.userQuery);

                    const noResultsMessage: ChatMessage = {
                        id: this.generateId(),
                        role: 'assistant',
                        content: this.translateService.instant('chat.learning-assistant.no-results', { query: this.userQuery }),
                        timestamp: Date.now(),
                        type: 'text'
                    };

                    this.messageAdded.emit(noResultsMessage);
                    this.userQuery = '';
                    return;
                }

                // Hay resultados - agregar mensaje con cursos al historial
                const assistantMessage: ChatMessage = {
                    id: this.generateId(),
                    role: 'assistant',
                    content: this.translateService.instant('chat.learning-assistant.found-courses', { count: courses.length.toString() }),
                    timestamp: Date.now(),
                    type: 'course-suggestion',
                    courses: courses // Guardar los cursos en el mensaje
                };

                this.messageAdded.emit(assistantMessage);
                this.userQuery = '';

                // Hacer scroll al final para ver las nuevas recomendaciones
                setTimeout(() => this.scrollToBottom(), 300);
            },
            (error) => {
                this.isLoading = false;
                console.error('❌ Error obtaining course suggestions:', error);

                // Crear mensaje de error detallado basado en el tipo de error
                let errorContent = this.translateService.instant('chat.learning-assistant.sorry-no-courses') + ' ';
                const errorStatus = error?.status;
                const errorData = error?.error;

                if (errorStatus === 0 || errorStatus === undefined) {
                    console.warn('📡 Connection error detected');
                    errorContent += this.translateService.instant('chat.learning-assistant.connection-error');
                } else if (errorStatus === 400) {
                    console.warn('📝 Bad request (400)');
                    errorContent += this.translateService.instant('chat.learning-assistant.invalid-search');
                } else if (errorStatus === 401) {
                    console.warn('🔐 Unauthorized (401)');
                    errorContent += this.translateService.instant('chat.learning-assistant.permission-denied');
                } else if (errorStatus === 403) {
                    console.warn('🚫 Forbidden (403)');
                    errorContent += this.translateService.instant('chat.learning-assistant.access-denied');
                } else if (errorStatus === 404) {
                    console.warn('🔍 Not found (404)');
                    errorContent += this.translateService.instant('chat.learning-assistant.service-unavailable');
                } else if (errorStatus === 429) {
                    console.warn('⚠️ Rate limit exceeded (429)');
                    errorContent = this._buildTokenLimitExceededMessage(errorData);
                } else if (errorStatus === 500) {
                    console.warn('⚙️ Server error (500)');
                    errorContent += this.translateService.instant('chat.learning-assistant.server-error');
                } else if (errorStatus === 503) {
                    console.warn('🔧 Service unavailable (503)');
                    errorContent += this.translateService.instant('chat.learning-assistant.service-maintenance');
                } else if (error?.statusText === 'Unknown Error') {
                    console.warn('🌐 Network error');
                    errorContent += this.translateService.instant('chat.learning-assistant.network-error');
                } else {
                    console.warn('❓ Unknown error:', error?.message);
                    errorContent += this.translateService.instant('chat.learning-assistant.try-again');
                }

                const errorMessage: ChatMessage = {
                    id: this.generateId(),
                    role: 'assistant',
                    content: errorContent,
                    timestamp: Date.now(),
                    type: 'text'
                };

                this.messageAdded.emit(errorMessage);
                this.userQuery = '';
            }
        );
    }

    /**
     * Maneja el clic en Enter en el input
     */
    onKeyPress(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.sendQuery();
        }
    }

    /**
     * Maneja la selección de un curso
     */
    selectCourse(course: Course): void {
        this.selectedCourse = course;
        console.log('📋 Curso seleccionado:', {
            title: course.title,
            locale: course.locale,
            marketTrends: course.marketTrends,
            professionalExpansion: course.professionalExpansion
        });
        this.courseSelected.emit(true); // Notificar que se seleccionó un curso
    }

    /**
     * Vuelve a la lista de cursos
     */
    backToList(): void {
        this.selectedCourse = null;
        this.courseSelected.emit(false); // Notificar que se deseleccionó el curso

        // Hacer scroll al final para ver las recomendaciones
        setTimeout(() => this.scrollToBottom(), 200);
    }

    /**
     * Suscribe el usuario a un curso navegando al detalle del curso
     */
    subscribeToCourse(course: Course): void {
        if (this.isSubscribing) {
            return; // Evitar múltiples clicks
        }

        this.isSubscribing = true;

        // Mensaje de usuario
        const userMessage: ChatMessage = {
            id: this.generateId(),
            role: 'user',
            content: this.translateService.instant('chat.learning-assistant.want-to-subscribe', { title: course.title }),
            timestamp: Date.now(),
            type: 'text'
        };

        this.messageAdded.emit(userMessage);

        // Mensaje de confirmación
        const confirmMessage: ChatMessage = {
            id: this.generateId(),
            role: 'assistant',
            content: this.translateService.instant('chat.learning-assistant.subscription-message', { title: course.title }),
            timestamp: Date.now(),
            type: 'text'
        };

        this.messageAdded.emit(confirmMessage);

        // Esperar un poco y luego navegar al detalle del curso
        setTimeout(() => {
            const courseIdToUse = course.contentId || course.id;
            const contentType = course.contentType || 'instance';
            const provider = course.provider;
            const templateId = course.templateId; // Pasar template_id para cursos

            this.chatAIService.navigateToCourseDetail(courseIdToUse, contentType, provider, templateId);

            const navigationMessage: ChatMessage = {
                id: this.generateId(),
                role: 'assistant',
                content: this.translateService.instant('chat.learning-assistant.navigation-message'),
                timestamp: Date.now(),
                type: 'text'
            };

            this.messageAdded.emit(navigationMessage);
            this.selectedCourse = null;
            this.isSubscribing = false;

            // Hacer scroll al final después de completar la suscripción
            setTimeout(() => this.scrollToBottom(), 100);
        }, 1000);
    }

    /**
     * Construye un mensaje detallado cuando se excede el límite de tokens
     */
    private _buildTokenLimitExceededMessage(errorData: any): string {
        if (!errorData) {
            return this.translateService.instant('chat.learning-assistant.token-limit-exceeded');
        }

        const limitType = errorData?.message || '';
        const currentUsage = errorData?.currentUsage || {};
        const limits = errorData?.limits || {};

        let limitNameKey = 'consumo de tokens';
        let currentValue = 0;
        let maxValue = 0;
        let availabilityKey = '';

        // Determinar qué límite fue excedido
        if (limitType.includes('daily_limit_exceeded')) {
            limitNameKey = this.translateService.instant('chat.learning-assistant.daily-limit');
            currentValue = currentUsage.today || 0;
            maxValue = limits.daily || 0;
            availabilityKey = 'chat.learning-assistant.available-tomorrow';
        } else if (limitType.includes('weekly_limit_exceeded')) {
            limitNameKey = this.translateService.instant('chat.learning-assistant.weekly-limit');
            currentValue = currentUsage.thisWeek || 0;
            maxValue = limits.weekly || 0;
            availabilityKey = 'chat.learning-assistant.available-next-week';
        } else if (limitType.includes('monthly_limit_exceeded')) {
            limitNameKey = this.translateService.instant('chat.learning-assistant.monthly-limit');
            currentValue = currentUsage.thisMonth || 0;
            maxValue = limits.monthly || 0;
            availabilityKey = 'chat.learning-assistant.available-next-month';
        }

        // Construir el mensaje con traducciones
        let message = this.translateService.instant('chat.learning-assistant.token-limit-title') + '<br><br>';
        message += this.translateService.instant('chat.learning-assistant.reached-limit', { limitName: limitNameKey }) + '<br>';
        message += this.translateService.instant('chat.learning-assistant.current-usage', { current: this._formatNumber(currentValue), max: this._formatNumber(maxValue) }) + '<br><br>';
        message += this.translateService.instant('chat.learning-assistant.consumption-details') + '<br>';
        message += this.translateService.instant('chat.learning-assistant.today', { today: this._formatNumber(currentUsage.today || 0) }) + '<br>';
        message += this.translateService.instant('chat.learning-assistant.this-week', { week: this._formatNumber(currentUsage.thisWeek || 0) }) + '<br>';
        message += this.translateService.instant('chat.learning-assistant.this-month', { month: this._formatNumber(currentUsage.thisMonth || 0) }) + '<br><br>';
        message += this.translateService.instant(availabilityKey);

        return message;
    }

    /**
     * Formatea un número con separadores de miles
     */
    private _formatNumber(num: number): string {
        return num.toLocaleString('es-ES');
    }

    /**
     * Genera un ID único para los mensajes
     */
    private generateId(): string {
        return `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Detecta cambios en los inputs (especialmente en messages)
     */
    ngOnChanges(changes: SimpleChanges): void {
        // Solo hacer scroll si cambiaron los mensajes
        if (changes['messages'] && changes['messages'].currentValue) {
            // Esperar a que Angular renderice los cambios antes de hacer scroll
            setTimeout(() => this.scrollToBottom(), 200);
        }
    }

    /**
     * Hace scroll al final del contenedor de contenido
     */
    private scrollToBottom(): void {
        try {
            if (this.contentContainer && this.contentContainer.nativeElement) {
                const element = this.contentContainer.nativeElement;
                element.scrollTop = element.scrollHeight;
            }
        } catch (err) {
            // Ignorar errores de scroll
        }
    }
}
